<template>
  <section class="">
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-12 lg:pt-16',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'
      ]"
    >
      <div class="">
        <div class="flex flex-col gap-4 rounded-[15px] bg-[#FDF6F2] p-8 sm:p-[45px]">
          <h2
            v-if="title"
            class="text-center text-[11px] leading-[18.7px] tracking-[1.43px] uppercase text-[#F1574B] font-ww-regular"
            v-html="title"
          />

          <div class="flex flex-col gap-5 pt-1 lowercase">
            <a
              v-for="(link, index) in primaryLinks"
              :key="`primary-${link.url}-${index}`"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="block text-[11.5px] leading-[19.55px] tracking-[0.46px] text-[#4A4A4A] underline break-all font-ww-regular"
            >{{ link.label }}</a>

            <transition
              v-if="hasMoreLinks"
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              @enter="onEnter"
              @after-enter="onAfterEnter"
              @leave="onLeave"
            >
              <div
                v-show="isExpanded"
                class="flex flex-col gap-5"
              >
                <a
                  v-for="(link, index) in extraLinks"
                  :key="`extra-${link.url}-${index}`"
                  :href="link.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block text-[11.5px] leading-[19.55px] tracking-[0.46px] text-[#4A4A4A] underline break-all font-ww-regular"
                >{{ link.label }}</a>
              </div>
            </transition>

            <div
              v-if="hasMoreLinks"
              class="flex flex-col items-center gap-3 pt-2"
            >
              <div
                v-if="!isExpanded"
                class="w-full h-px bg-gradient-to-r from-transparent via-[#E0D5C5] to-transparent transition-opacity duration-300"
                aria-hidden="true"
              />

              <button
                type="button"
                class="inline-flex gap-2 items-center px-5 py-2.5 text-xs tracking-wide text-lifeboost-red rounded-full border border-[#E0D5C5] bg-white font-ww-bold transition-colors hover:border-lifeboost-red/40 hover:bg-[#FFFBF8] focus:outline-none focus-visible:ring-2 focus-visible:ring-lifeboost-red/30"
                :aria-expanded="isExpanded"
                @click="isExpanded = !isExpanded"
              >
                <span>{{ isExpanded ? showLessText : showMoreText }}</span>
                <svg
                  class="shrink-0 size-3.5 transition-transform duration-300 ease-out"
                  :class="isExpanded ? 'rotate-180' : ''"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const mensHealthReferences = [
  'https://www.uchicagomedicineadventhealth.org/blog/8-medical-conditions-affect-men-more-women',
  'https://www.nia.nih.gov/news/mild-cognitive-impairment-more-common-older-men-older-women',
  'https://www.sciencefocus.com/news/women-brain-age-men-dementia-alzheimers',
  'https://www.medstarhealth.org/blog/heart-disease-men',
  'https://www.cms.gov/Outreach-and-Education/Medicare-Learning-Network-MLN/MLNProducts/Downloads/June2012-Mens-Health-Week.pdf',
  'https://www.cdc.gov/heart-disease/about/men-and-heart-disease.html',
  'https://www.nia.nih.gov/health/heart-health/heart-health-and-aging',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC10262944/',
  'https://www.health.harvard.edu/healthy-aging-and-longevity/drinking-coffee-linked-to-healthier-hearts-and-longer-lives',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC3184682/',
  'https://www.goodrx.com/well-being/supplements-herbs/nitric-oxide-supplement',
  'https://resperate.com/blog/hypertension/diet/healthy-foods/nattokinase-for-a-healthy-heart-and-much-more',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC6043915/',
  'https://www.hopkinsmedicine.org/health/wellness-and-prevention/the-heart-test-you-may-need-but-likely-havent-heard-of',
  'https://www.heart.org/en/news/2022/04/06/walking-your-way-to-better-health-remember-the-acronym-fit#:~:text=Pace%20matters.,%22But%20build%20up%20slowly.%22',
  'https://www.mayoclinic.org/healthy-lifestyle/fitness/in-depth/walking/art-20046261',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC4783196/',
  'https://www.sciencefocus.com/news/women-brain-age-men-dementia-alzheimers',
  'https://www.healthline.com/nutrition/omega-3-fish-oil-for-brain-health#TOC_TITLE_HDR_5',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC9641984/',
  'https://www.healthline.com/nutrition/coffee-brain#memory',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC10479851/',
  'https://www.gutmicrobiotaforhealth.com/the-role-of-probiotics-in-ageing-and-longevity/#:~:text=They%20may%20restore%20a%20healthy,promote%20health%20in%20the%20elderly.',
  'https://www.nm.org/healthbeat/healthy-tips/nutrition/probiotics-and-your-gut',
  'https://health.clevelandclinic.org/butyrate-benefits',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC12029953/',
  'https://stonestreetcoffee.com/blogs/brooklyn-coffee-academy/is-your-coffee-too-acidic',
  'https://www.texashealth.org/areyouawellbeing/Mens-Health/Mens-Health-in-2025-Boosting-Metabolism-and-Muscle-Health',
  'https://www.sciencedirect.com/science/article/pii/S0002916523290450',
  'https://www.betterhealth.vic.gov.au/health/healthyliving/resistance-training-health-benefits',
  'https://www.issaonline.com/blog/post/muscle-protein-synthesis-what-it-is-and-how-to-maximize-it',
  'https://hartfordhospital.org/services/bone-joint-institute/news/news-detail?articleid=65418',
  'https://www.healthline.com/nutrition/26-muscle-building-foods#muscle-building-foods',
  'https://www.mayoclinic.org/drugs-supplements-creatine/art-20347591',
  'https://psychiatryonline.org/doi/10.1176/appi.ps.52.3.291',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC6835548/',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC9789013/',
  'https://www.nia.nih.gov/health/prostate-health/prostate-problems',
  'https://gamedaymenshealth.com/blog/vascular-function-and-sexual-health-understanding-the-connection',
  'https://www.ahajournals.org/doi/10.1161/circulationaha.110.017681',
  'https://www.bswhealth.com/blog/4-ways-testosterone-and-erectile-function-connect-to-your-heart-health',
  'https://www.penilerehabilitation.clinic/post/the-surprising-benefits-of-regular-exercise-for-sexual-function',
  'https://www.mayoclinic.org/healthy-lifestyle/mens-health/in-depth/male-menopause/art-20048056',
  'https://drsebis.com/blogs/journal/powering-up-tips-and-tricks-for-enhancing-mitochondrial-health',
  'https://www.health.harvard.edu/healthbeat/fight-fatigue-with-fluids',
  'https://www.healthline.com/nutrition/top-evidence-based-health-benefits-of-coffee',
  'https://www.sleepfoundation.org/sleep-hygiene/sleep-satisfaction-and-energy-levels',
  'https://www.health.harvard.edu/healthbeat/how-sleep-boosts-your-energy',
  'https://www.mayoclinic.org/healthy-lifestyle/adult-health/expert-answers/how-many-hours-of-sleep-are-enough/faq-20057898',
  'https://www.tsh.org/nutrition-tips-for-mens-health/',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC7700832/',
  'https://www.healthline.com/health/how-often-should-you-work-out',
  'https://oregonmanclinics.com/blog/primary-care-mens-health/mens-health-how-to-improve-your-positivity-this-positive-attitude-month/#:~:text=It\'s%20no%20secret%20that%20having,stroke%2C%20and%20other%20health%20problems.',
  'https://www.goodrx.com/well-being/supplements-herbs/nitric-oxide-supplement',
  'https://www.nad.com/news/new-study-reveals-high-dose-supplement-shrinks-arterial-plaque-by-36',
  'https://www.healthline.com/nutrition/how-much-omega-3#excessive-intake',
  'https://stjosephshealth.org/healthy-you/love-word-games-heres-why-they-boost-your-brain-health/#:~:text=The%20Science%20Behind%20Brain-Boosting,focus%2C%20and%20solve%20problems.%E2%80%9D',
  'https://www.nordic.com/healthy-science/probiotics-how-many-billion-cfu-do-i-need-to-maintain-daily-digestive-health/#:~:text=Thus%2C%20for%20individuals%20in%20relatively,7%2C10%2C14-18',
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC7294979/',
  'https://www.health.harvard.edu/exercise-and-fitness/what-is-creatine-potential-benefits-and-risks-of-this-popular-supplement',
  'https://www.hopkinsmedicine.org/health/wellness-and-prevention/9-reasons-why-the-right-amount-of-coffee-is-good-for-you',
  'https://www.acc.org/latest-in-cardiology/articles/2022/03/25/17/36/coffee-a-day-may-benefit-the-heart-acc-2022'
]

</script>

<script setup>
const props = defineProps({
  title: {
    type: String,
    default: 'References'
  },
  links: {
    type: Array,
    default: () => mensHealthReferences
  },
  initialVisibleCount: {
    type: Number,
    default: 3
  },
  showMoreLabel: {
    type: String,
    default: 'Show all references'
  },
  showLessLabel: {
    type: String,
    default: 'Show fewer references'
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

const isExpanded = ref(false)

const normalizedLinks = computed(() =>
  props.links.map((link) => {
    if (typeof link === 'string') {
      return { url: link, label: link }
    }

    return {
      url: link.url,
      label: link.label || link.url
    }
  })
)

const initialCount = computed(() =>
  Math.max(0, Number(props.initialVisibleCount) || 0)
)

const hasMoreLinks = computed(
  () => normalizedLinks.value.length > initialCount.value
)

const primaryLinks = computed(() => {
  if (!hasMoreLinks.value) {
    return normalizedLinks.value
  }

  return normalizedLinks.value.slice(0, initialCount.value)
})

const extraLinks = computed(() => {
  if (!hasMoreLinks.value) {
    return []
  }

  return normalizedLinks.value.slice(initialCount.value)
})

const hiddenCount = computed(() =>
  Math.max(0, normalizedLinks.value.length - initialCount.value)
)

const showMoreText = computed(() => {
  if (hiddenCount.value > 0) {
    return `${props.showMoreLabel} (${hiddenCount.value} more)`
  }

  return props.showMoreLabel
})

const showLessText = computed(() => props.showLessLabel)

function onEnter(el) {
  el.style.height = '0px'
  el.style.overflow = 'hidden'
  el.style.opacity = '0'
  el.style.transition = 'height 300ms ease-out, opacity 250ms ease-out'

  requestAnimationFrame(() => {
    el.style.height = `${el.scrollHeight}px`
    el.style.opacity = '1'
  })
}

function onAfterEnter(el) {
  el.style.height = 'auto'
  el.style.overflow = 'visible'
  el.style.opacity = '1'
}

function onLeave(el) {
  el.style.height = `${el.scrollHeight}px`
  el.style.overflow = 'hidden'
  el.style.opacity = '1'
  el.style.transition = 'height 300ms ease-in, opacity 200ms ease-in'

  requestAnimationFrame(() => {
    el.style.height = '0px'
    el.style.opacity = '0'
  })
}

watch(
  () => props.links.length,
  () => {
    isExpanded.value = false
  }
)

defineBuilderComponent({
  friendlyName: 'Content - Article References',
  inputs: [
    {
      name: 'title',
      type: 'text',
      defaultValue: 'References'
    },
    {
      name: 'initialVisibleCount',
      type: 'number',
      defaultValue: 3
    },
    {
      name: 'showMoreLabel',
      type: 'text',
      defaultValue: 'Show all references'
    },
    {
      name: 'showLessLabel',
      type: 'text',
      defaultValue: 'Show fewer references'
    },
    {
      name: 'links',
      type: 'list',
      defaultValue: [
        'https://www.uchicagomedicineadventhealth.org/blog/8-medical-conditions-affect-men-more-women',
        'https://www.nia.nih.gov/news/mild-cognitive-impairment-more-common-older-men-older-women',
        'https://www.sciencefocus.com/news/women-brain-age-men-dementia-alzheimers',
        'https://www.medstarhealth.org/blog/heart-disease-men',
        'https://www.cms.gov/Outreach-and-Education/Medicare-Learning-Network-MLN/MLNProducts/Downloads/June2012-Mens-Health-Week.pdf',
        'https://www.cdc.gov/heart-disease/about/men-and-heart-disease.html',
        'https://www.nia.nih.gov/health/heart-health/heart-health-and-aging',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC10262944/',
        'https://www.health.harvard.edu/healthy-aging-and-longevity/drinking-coffee-linked-to-healthier-hearts-and-longer-lives',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC3184682/',
        'https://www.goodrx.com/well-being/supplements-herbs/nitric-oxide-supplement',
        'https://resperate.com/blog/hypertension/diet/healthy-foods/nattokinase-for-a-healthy-heart-and-much-more',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC6043915/',
        'https://www.hopkinsmedicine.org/health/wellness-and-prevention/the-heart-test-you-may-need-but-likely-havent-heard-of',
        'https://www.heart.org/en/news/2022/04/06/walking-your-way-to-better-health-remember-the-acronym-fit#:~:text=Pace%20matters.,%22But%20build%20up%20slowly.%22',
        'https://www.mayoclinic.org/healthy-lifestyle/fitness/in-depth/walking/art-20046261',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC4783196/',
        'https://www.sciencefocus.com/news/women-brain-age-men-dementia-alzheimers',
        'https://www.healthline.com/nutrition/omega-3-fish-oil-for-brain-health#TOC_TITLE_HDR_5',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC9641984/',
        'https://www.healthline.com/nutrition/coffee-brain#memory',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC10479851/',
        'https://www.gutmicrobiotaforhealth.com/the-role-of-probiotics-in-ageing-and-longevity/#:~:text=They%20may%20restore%20a%20healthy,promote%20health%20in%20the%20elderly.',
        'https://www.nm.org/healthbeat/healthy-tips/nutrition/probiotics-and-your-gut',
        'https://health.clevelandclinic.org/butyrate-benefits',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC12029953/',
        'https://stonestreetcoffee.com/blogs/brooklyn-coffee-academy/is-your-coffee-too-acidic',
        'https://www.texashealth.org/areyouawellbeing/Mens-Health/Mens-Health-in-2025-Boosting-Metabolism-and-Muscle-Health',
        'https://www.sciencedirect.com/science/article/pii/S0002916523290450',
        'https://www.betterhealth.vic.gov.au/health/healthyliving/resistance-training-health-benefits',
        'https://www.issaonline.com/blog/post/muscle-protein-synthesis-what-it-is-and-how-to-maximize-it',
        'https://hartfordhospital.org/services/bone-joint-institute/news/news-detail?articleid=65418',
        'https://www.healthline.com/nutrition/26-muscle-building-foods#muscle-building-foods',
        'https://www.mayoclinic.org/drugs-supplements-creatine/art-20347591',
        'https://psychiatryonline.org/doi/10.1176/appi.ps.52.3.291',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC6835548/',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC9789013/',
        'https://www.nia.nih.gov/health/prostate-health/prostate-problems',
        'https://gamedaymenshealth.com/blog/vascular-function-and-sexual-health-understanding-the-connection',
        'https://www.ahajournals.org/doi/10.1161/circulationaha.110.017681',
        'https://www.bswhealth.com/blog/4-ways-testosterone-and-erectile-function-connect-to-your-heart-health',
        'https://www.penilerehabilitation.clinic/post/the-surprising-benefits-of-regular-exercise-for-sexual-function',
        'https://www.mayoclinic.org/healthy-lifestyle/mens-health/in-depth/male-menopause/art-20048056',
        'https://drsebis.com/blogs/journal/powering-up-tips-and-tricks-for-enhancing-mitochondrial-health',
        'https://www.health.harvard.edu/healthbeat/fight-fatigue-with-fluids',
        'https://www.healthline.com/nutrition/top-evidence-based-health-benefits-of-coffee',
        'https://www.sleepfoundation.org/sleep-hygiene/sleep-satisfaction-and-energy-levels',
        'https://www.health.harvard.edu/healthbeat/how-sleep-boosts-your-energy',
        'https://www.mayoclinic.org/healthy-lifestyle/adult-health/expert-answers/how-many-hours-of-sleep-are-enough/faq-20057898',
        'https://www.tsh.org/nutrition-tips-for-mens-health/',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC7700832/',
        'https://www.healthline.com/health/how-often-should-you-work-out',
        'https://oregonmanclinics.com/blog/primary-care-mens-health/mens-health-how-to-improve-your-positivity-this-positive-attitude-month/#:~:text=It\'s%20no%20secret%20that%20having,stroke%2C%20and%20other%20health%20problems.',
        'https://www.goodrx.com/well-being/supplements-herbs/nitric-oxide-supplement',
        'https://www.nad.com/news/new-study-reveals-high-dose-supplement-shrinks-arterial-plaque-by-36',
        'https://www.healthline.com/nutrition/how-much-omega-3#excessive-intake',
        'https://stjosephshealth.org/healthy-you/love-word-games-heres-why-they-boost-your-brain-health/#:~:text=The%20Science%20Behind%20Brain-Boosting,focus%2C%20and%20solve%20problems.%E2%80%9D',
        'https://www.nordic.com/healthy-science/probiotics-how-many-billion-cfu-do-i-need-to-maintain-daily-digestive-health/#:~:text=Thus%2C%20for%20individuals%20in%20relatively,7%2C10%2C14-18',
        'https://pmc.ncbi.nlm.nih.gov/articles/PMC7294979/',
        'https://www.health.harvard.edu/exercise-and-fitness/what-is-creatine-potential-benefits-and-risks-of-this-popular-supplement',
        'https://www.hopkinsmedicine.org/health/wellness-and-prevention/9-reasons-why-the-right-amount-of-coffee-is-good-for-you',
        'https://www.acc.org/latest-in-cardiology/articles/2022/03/25/17/36/coffee-a-day-may-benefit-the-heart-acc-2022'
      ],
      subFields: [
        {
          name: 'url',
          type: 'url',
          defaultValue: ''
        },
        {
          name: 'label',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>

